import { FormControlLabel, Switch } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';

import { useQueryUserData } from '~/data/user';

import ThirdPartyService from '~/services/thirdParty.service';

import { Icon } from '@vestigas/ui';
import { Button } from '@vestigas/ui/molecules';

import { PRIVACY_POLICY_URL } from '../constants';

import { ExternalLegalLink } from './ExternalLegalLink';
import { LegalArticle, LegalSection } from './LegalArticle';

const bodyTextClassName = 'text-sm leading-relaxed break-words text-gray-600';

function handleResetClick() {
  ThirdPartyService.optInProductAnalyticsTools();

  globalThis.location.reload();
}

export function PrivacyContent() {
  const { t } = useTranslation('legal');

  const { data: currentUser } = useQueryUserData(true);
  const featureFlags = currentUser?.featureFlags ?? {};

  const hasProductAnalyticsConsent =
    !ThirdPartyService.productAnalyticsToolsOptedOut();

  const handleConsentChange = () => {
    if (hasProductAnalyticsConsent) {
      ThirdPartyService.optOutProductAnalyticsTools();
    } else {
      ThirdPartyService.optInProductAnalyticsTools();
    }

    // Reload so the analytics tools are (de)initialized with the new consent.
    globalThis.location.reload();
  };

  return (
    <LegalArticle title={t(($) => $.privacy.title)}>
      <LegalSection heading={t(($) => $.privacy.statement.heading)}>
        <p className={bodyTextClassName}>
          <Trans
            components={{
              websiteLink: <ExternalLegalLink href={PRIVACY_POLICY_URL} />,
            }}
            i18nKey={($) => $.privacy.statement.body}
            ns="legal"
          />
        </p>
      </LegalSection>

      <LegalSection heading={t(($) => $.privacy.cookiesHeading)}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-medium text-tiga-text-default">
              {t(($) => $.privacy.cookieUsage.heading)}
            </h3>
            <p className={bodyTextClassName}>
              {t(($) => $.privacy.cookieUsage.paragraph1)}
            </p>
            <p className={bodyTextClassName}>
              <Trans
                components={{
                  websiteLink: <ExternalLegalLink href={PRIVACY_POLICY_URL} />,
                }}
                i18nKey={($) => $.privacy.cookieUsage.paragraph2}
                ns="legal"
              />
            </p>
            <p className={bodyTextClassName}>
              <Trans
                components={{ bold: <b /> }}
                i18nKey={($) => $.privacy.cookieUsage.paragraph3}
                ns="legal"
              />
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-medium text-tiga-text-default">
              {t(($) => $.privacy.cookieServices.heading)}
            </h3>
            <p className={bodyTextClassName}>
              {t(($) => $.privacy.cookieServices.body)}
            </p>
          </div>

          <div>
            <FormControlLabel
              disabled
              control={<Switch defaultChecked />}
              label={t(($) => $.privacy.essentialCookies.label)}
            />
            <p className={bodyTextClassName}>
              {t(($) => $.privacy.essentialCookies.body)}
            </p>
          </div>

          {featureFlags.productAnalyticsTools ? (
            <div>
              <FormControlLabel
                checked={hasProductAnalyticsConsent}
                control={<Switch />}
                label={t(($) => $.privacy.analyticsCookies.label)}
                name="product_analytics_consent"
                onChange={handleConsentChange}
              />
              <p className={bodyTextClassName}>
                {t(($) => $.privacy.analyticsCookies.body)}
              </p>
              <ul className={`list-disc pl-6 ${bodyTextClassName}`}>
                <li>
                  <b>{t(($) => $.privacy.analyticsCookies.amplitudeName)}</b>
                  <br />
                  {t(($) => $.privacy.analyticsCookies.amplitudeBody)}
                </li>
              </ul>
            </div>
          ) : null}

          <div>
            <Button
              icon={<Icon metaphor="delete" size="inherit" />}
              text={t(($) => $.privacy.resetCookies.text)}
              title={t(($) => $.privacy.resetCookies.toolTip)}
              variant="outlined"
              onClick={handleResetClick}
            />
          </div>
        </div>
      </LegalSection>
    </LegalArticle>
  );
}
